import { Theme } from '@mui/material/styles';

// Common style tokens and patterns
export const styleTokens = {
  // Animation and transitions
  transitions: {
    default: 'all 0.2s ease-in-out',
    card: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    smooth: 'all 0.3s ease',
  },

  // Elevations and shadows
  elevations: {
    card: '0px 4px 8px rgba(0, 0, 0, 0.08)',
    cardHover: '0px 12px 24px rgba(0, 0, 0, 0.15)',
    focus: '0 0 0 2px rgba(86, 147, 247, 0.2)',
    listItem: '0 2px 8px rgba(0, 0, 0, 0.1)',
    listItemSelected: '0 2px 8px rgba(86, 147, 247, 0.3)',
    listItemSelectedHover: '0 4px 12px rgba(86, 147, 247, 0.4)',
  },

  // Color patterns
  colors: {
    primaryAlpha: {
      4: 'rgba(86, 147, 247, 0.04)',
      8: 'rgba(86, 147, 247, 0.08)',
      20: 'rgba(86, 147, 247, 0.2)',
      42: 'rgb(86 147 247 / 42%)',
      62: 'rgb(86 147 247 / 62%)',
    },
    errorAlpha: {
      10: 'rgba(195, 62, 56, 0.1)',
    },
    border: {
      light: 'rgba(0, 0, 0, 0.08)',
      medium: 'rgba(0, 0, 0, 0.12)',
    },
  },

  // Background patterns
  backgrounds: {
    cardGradient: 'linear-gradient(145deg, #ffffff 0%, #fafafa 100%)',
  },

  // Transform patterns
  transforms: {
    hover: 'translateY(-3px) scale(1.01)',
    slideRight: (amount: number) => `translateX(${amount}px)`,
    scaleUp: 'scale(1.01)',
  },
} as const;

// Common reusable style mixins
export const commonStyles = {
  // Enhanced card with hover effects
  enhancedCard: (theme: Theme) => ({
    transition: styleTokens.transitions.card,
    background: styleTokens.backgrounds.cardGradient,
    border: `1px solid ${styleTokens.colors.border.light}`,
    boxShadow: styleTokens.elevations.card,
    
    '&:hover': {
      transform: styleTokens.transforms.hover,
      boxShadow: styleTokens.elevations.cardHover,
      borderColor: styleTokens.colors.primaryAlpha[20],
    },
  }),

  // Fixed height card for consistent layouts
  fixedHeightCard: (theme: Theme, height: string = '400px') => ({
    display: 'flex',
    flexDirection: 'column' as const,
    height,
  }),

  // Standard card dimensions matching package card layout
  standardCardDimensions: (theme: Theme) => ({
    display: 'flex',
    flexDirection: 'column' as const,
    
    [theme.breakpoints.up('sm')]: {
      height: 300,
      minHeight: 300
    },
    
    [theme.breakpoints.down('sm')]: {
      height: 'auto',
      minHeight: 200
    },
  }),

  // Interactive list item with enhanced states
  enhancedListItem: (theme: Theme) => ({
    position: 'relative' as const,
    transition: styleTokens.transitions.default,
    borderRadius: theme.spacing(0.75),
    
    '&:hover': {
      backgroundColor: styleTokens.colors.primaryAlpha[4],
      borderRadius: theme.spacing(0.75),
    },
    
    '&:focus-within': {
      backgroundColor: styleTokens.colors.primaryAlpha[8],
      transform: styleTokens.transforms.scaleUp,
      boxShadow: styleTokens.elevations.focus,
    },
  }),

  // Interactive button with enhanced states
  enhancedButton: (theme: Theme) => ({
    transition: styleTokens.transitions.default,
    
    '&:hover': {
      transform: styleTokens.transforms.scaleUp,
      // Remove background color change to maintain button's original color
      '& .MuiSvgIcon-root': {
        color: theme.palette.primary.main,
      }
    }
  }),

  // Enhanced form controls with hover effects
  enhancedFormControl: (theme: Theme) => ({
    transition: styleTokens.transitions.default,
    
    '&:hover': {
      boxShadow: styleTokens.elevations.listItem,
    },
    
    '&.Mui-focused': {
      boxShadow: styleTokens.elevations.focus,
    },
  }),

  // Responsive typography
  responsiveText: (theme: Theme) => ({
    fontSize: '0.875rem',
    [theme.breakpoints.up('sm')]: {
      fontSize: '1rem',
    },
  }),

  // Responsive width for form inputs
  responsiveInputWidth: (theme: Theme, defaultWidth: string) => ({
    width: defaultWidth,
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  }),

  // Responsive flex container
  responsiveFlexContainer: (theme: Theme) => ({
    display: 'flex',
    flexDirection: 'row' as const,
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
    flexWrap: 'wrap' as const,
    
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column' as const,
      alignItems: 'flex-start',
      gap: 8,
    },
  }),

  // Consistent card content padding
  standardCardPadding: () => ({
    padding: '16px !important',
  }),

  // Card content with overflow hidden
  cardContentWithOverflow: () => ({
    padding: '16px !important',
    overflow: 'hidden' as const,
  }),

  // Scroll container pattern
  scrollContainer: (maxHeight: string = '375px') => ({
    '& .scroll-container': {
      flexGrow: 1,
      overflowY: 'auto' as const,
      maxHeight,
    },
  }),

  // Form control label responsive styles
  responsiveFormLabel: (theme: Theme) => ({
    marginRight: 0,
    marginBottom: 0,
    
    [theme.breakpoints.up('sm')]: {
      marginRight: theme.spacing(2),
    },
    
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(0.5),
    },
    
    '& .MuiFormControlLabel-label': {
      fontSize: '0.875rem',
      fontWeight: 500,
      [theme.breakpoints.up('sm')]: {
        fontSize: '1rem',
      },
    },
  }),

  // Enhanced checkbox with hover effects
  enhancedCheckbox: () => ({
    '& .MuiCheckbox-root': {
      transition: styleTokens.transitions.default,
      
      '&:hover': {
        backgroundColor: styleTokens.colors.primaryAlpha[8],
        transform: 'scale(1.05)',
      },
    },
  }),

  // Selection states for list items
  listItemSelection: (theme: Theme) => ({
    '&.Mui-selected': {
      backgroundColor: styleTokens.colors.primaryAlpha[62],
      borderLeft: `4px solid ${theme.palette.primary.main}`,
      transform: styleTokens.transforms.slideRight(4),
      boxShadow: styleTokens.elevations.listItemSelected,
    },
    
    '&.Mui-selected:hover': {
      backgroundColor: styleTokens.colors.primaryAlpha[42],
      transform: styleTokens.transforms.slideRight(6),
      boxShadow: styleTokens.elevations.listItemSelectedHover,
    },
  }),

  // Empty state styling
  emptyState: (theme: Theme) => ({
    textAlign: 'center' as const,
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    color: theme.palette.text.secondary,
  }),

  // Responsive padding
  responsivePadding: (theme: Theme) => ({
    padding: theme.spacing(1, 1.5),
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(1.5, 2),
    },
  }),

  // Typography variants
  warningText: (theme: Theme) => ({
    color: theme.palette.warning.main,
    marginLeft: '15px',
  }),

  sectionText: (theme: Theme) => ({
    margin: theme.spacing(1),
    fontWeight: 500,
  }),

  brandText: () => ({
    fontWeight: 500,
    color: '#143A6B',
  }),
} as const;

// Utility functions for common patterns
export const createConditionalStyles = {
  // Conditional text color based on enabled state
  enabledText: (theme: Theme, enabled: boolean) => ({
    color: enabled ? theme.palette.text.primary : theme.palette.text.secondary,
    fontWeight: enabled ? 500 : 400,
    opacity: enabled ? 1 : 0.6,
  }),

  // Conditional hover effects
  conditionalHover: (enabled: boolean, hoverStyles: object) => 
    enabled ? { '&:hover': hoverStyles } : {},
};
